import { useMemo } from 'react';
import type { ReactNode } from 'react';
import { useBrainStore } from '@/state/brainStore';
import type { StudioId } from '@/domain/types';
import type { ReportingScope } from '@/domain/reportingCompare';
import { perStudioSeries, singleStudioScope } from '@/domain/reportingCompare';
import { Figure, Money } from '@/components/reporting/Money';
import InfoTip from '@/components/InfoTip';
import {
  revenueByStore, depositsReceivedIn, weeklyCompletedTally, depositPool,
  rebookRate, newLeadsByDay, weeksBookedAhead, kpiTiles, previousWindowDays,
  queueHealthByStudio, unworkedLeads, reportingNow,
} from '@/domain/reportingEngine';
import { moneyAtRisk } from '@/domain/reportingMoney';
import { formatStudioDay } from '@/utils/studioDay';
import { relativeFromIso } from '@/utils/format';

function summaryFor(days: readonly string[], sid: StudioId | undefined) {
  const rev = revenueByStore(days, sid);
  const oursAud = rev.reduce((a, r) => a + r.oursAud, 0);
  const totalAud = rev.reduce((a, r) => a + r.totalAud, 0);
  const dep = depositsReceivedIn(days, sid);
  const completed = weeklyCompletedTally(days, sid);
  const pool = depositPool(sid);
  const rb = rebookRate(days, sid);
  const leads = newLeadsByDay(days, sid).reduce((a, d) => a + d.count, 0);
  const weeks = weeksBookedAhead(sid).weeks;
  const avg = kpiTiles(days, sid).avgBookingValueAud;
  return { oursAud, totalAud, depAud: dep.totalAud, completed, poolAud: pool.heldAud, poolCount: pool.count, rebookRate: rb.rate, leads, weeks, avg };
}

export default function SummaryTab({ scope }: { scope: ReportingScope }) {
  const bookings = useBrainStore((s) => s.bookings);
  const deposits = useBrainStore((s) => s.deposits);
  const customers = useBrainStore((s) => s.customers);
  const studios = useBrainStore((s) => s.studios);
  const salePayments = useBrainStore((s) => s.salePayments);
  const artists = useBrainStore((s) => s.artists);
  const pieces = useBrainStore((s) => s.pieces);
  const rebookFollowupTasks = useBrainStore((s) => s.rebookFollowupTasks);
  const { days, studioIds, compareMode, moneyVisible } = scope;

  const single = singleStudioScope(studioIds);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const one = useMemo(
    () => summaryFor(days, single),
    [days, single, bookings, deposits, customers, salePayments, artists, studios, pieces],
  );
  // Prior equal-length window, for the week-over-week delta badges.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const prev = useMemo(
    () => summaryFor(previousWindowDays(days), single),
    [days, single, bookings, deposits, customers, salePayments, artists, studios, pieces],
  );
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const series = useMemo(
    () => (compareMode ? perStudioSeries(studioIds, (sid) => summaryFor(days, sid)) : []),
    [compareMode, studioIds, days, bookings, deposits, customers, salePayments, artists, studios, pieces],
  );

  const pct = (n: number) => `${Math.round(n * 100)}%`;

  // What's slipping (75nd): money at risk, the live queues and untouched leads.
  const sequenceState = useBrainStore((s) => s.sequenceState);
  const confirmationCalls = useBrainStore((s) => s.confirmationCalls);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const slipping = useMemo(() => {
    if (compareMode) return null; // compare mode shows the table only
    const now = reportingNow();
    return {
      risk: moneyAtRisk(days, single, now),
      queues: queueHealthByStudio(now, single).filter((q) => studioIds.length === 0 || studioIds.includes(q.studioId)),
      leads: unworkedLeads(single),
    };
  }, [compareMode, days, single, studioIds, bookings, deposits, customers, studios, artists, sequenceState, confirmationCalls, pieces, rebookFollowupTasks]);

  if (compareMode) {
    const metrics: Array<{ label: string; money?: boolean; get: (s: ReturnType<typeof summaryFor>) => number; fmt?: (n: number) => string }> = [
      { label: 'Revenue', money: true, get: (s) => s.totalAud },
      { label: 'Studio take', money: true, get: (s) => s.oursAud },
      { label: 'Deposits received', money: true, get: (s) => s.depAud },
      { label: 'Bookings completed', get: (s) => s.completed },
      { label: 'Rebook rate', get: (s) => s.rebookRate, fmt: pct },
      { label: 'New leads', get: (s) => s.leads },
      { label: 'Weeks booked ahead', get: (s) => s.weeks, fmt: (n) => n.toFixed(1) },
      { label: 'Avg booking value', money: true, get: (s) => s.avg },
    ];
    return (
      <div className="space-y-4 max-w-6xl" data-testid="reporting-summary">
        <section className="panel p-4 overflow-x-auto">
          <h2 className="heading-serif text-lg text-cream mb-4">Studios compared, this period</h2>
          <table className="w-full text-sm" data-testid="summary-compare-table">
            <thead>
              <tr>
                <th className="text-left label-small-caps pb-2">Metric</th>
                {series.map((s) => (
                  <th key={s.studioId} className="text-right label-small-caps pb-2">{studios[s.studioId]?.name ?? s.studioId}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {metrics.map((m) => (
                <tr key={m.label} className="border-t border-gold-dim/15">
                  <td className="py-2 text-cream-soft">{m.label}</td>
                  {series.map((s) => {
                    const v = m.get(s.value);
                    return (
                      <td key={s.studioId} className="py-2 text-right font-mono text-cream">
                        {m.money ? <Money amountAud={v} visible={moneyVisible} /> : m.fmt ? m.fmt(v) : v.toLocaleString('en-AU')}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      </div>
    );
  }

  const windowLabel =
    days.length > 0
      ? `${formatStudioDay(days[0]!, 'EEE d MMM')} – ${formatStudioDay(days[days.length - 1]!, 'EEE d MMM')}`
      : '';

  return (
    <div className="space-y-6 max-w-6xl" data-testid="reporting-summary">
      <p className="font-inter text-xs text-cream-soft" data-testid="summary-window">
        This period: <span className="text-cream">{windowLabel}</span>
      </p>
      <section className="grid grid-cols-3 gap-3">
        <Tile label="Revenue · this period" delta={<DeltaBadge testid="summary-delta-revenue" cur={one.totalAud} prev={prev.totalAud} />}>
          <Money amountAud={one.totalAud} visible={moneyVisible} />
        </Tile>
        <Tile label="Deposits received" delta={<DeltaBadge testid="summary-delta-deposits" cur={one.depAud} prev={prev.depAud} />}>
          <Figure testId="summary-deposits-received" amountAud={one.depAud}>
            <Money amountAud={one.depAud} visible={moneyVisible} />
          </Figure>
        </Tile>
        <Tile label="Bookings completed" delta={<DeltaBadge testid="summary-delta-completed" cur={one.completed} prev={prev.completed} />}>
          {one.completed.toLocaleString('en-AU')}
        </Tile>
      </section>
      <section className="grid grid-cols-3 gap-3">
        <Tile label="Studio take (house)" delta={<DeltaBadge testid="summary-delta-studio-take" cur={one.oursAud} prev={prev.oursAud} />}>
          <Money amountAud={one.oursAud} visible={moneyVisible} />
        </Tile>
        <Tile label="Held deposit pool" info="All outstanding deposits paid but not yet used — a live liability snapshot.">
          <Money amountAud={one.poolAud} visible={moneyVisible} /> <span className="text-cream-faint text-base">· {one.poolCount}</span>
        </Tile>
        <Tile label="Rebook rate" info="In-chair rebooks ÷ completed sittings. Baseline 50%." delta={<DeltaBadge testid="summary-delta-rebook" cur={one.rebookRate} prev={prev.rebookRate} />}>
          {pct(one.rebookRate)}
        </Tile>
        <Tile label="New leads" delta={<DeltaBadge testid="summary-delta-leads" cur={one.leads} prev={prev.leads} />}>
          {one.leads.toLocaleString('en-AU')}
        </Tile>
        <Tile label="Weeks booked ahead" info="Whole weeks each studio is ≥80% booked before the first week with free space.">{one.weeks.toFixed(1)}</Tile>
        <Tile label="Avg booking value" delta={<DeltaBadge testid="summary-delta-avg" cur={one.avg} prev={prev.avg} />}>
          <Money amountAud={one.avg} visible={moneyVisible} />
        </Tile>
      </section>

      {slipping ? (
      <section className="panel p-4" data-testid="summary-slipping">
        <h2 className="heading-serif text-lg text-cream mb-3">What&rsquo;s slipping</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <p className="label-small-caps">Money at risk · this period</p>
            <p className="font-cinzel text-2xl text-blood mt-1">
              <Figure testId="summary-slipping-risk" amountAud={slipping.risk.totalMoneyAud} count={slipping.risk.totalCount}>
                <Money amountAud={slipping.risk.totalMoneyAud} visible={moneyVisible} />
              </Figure>
            </p>
            <ul className="mt-2 space-y-0.5 font-inter text-xs text-cream-soft">
              {slipping.risk.parts.map((p) => (
                <li key={p.key}>
                  {p.label}: <span className="text-cream">{p.count}</span> ·{' '}
                  <Money amountAud={p.moneyAud} visible={moneyVisible} />
                </li>
              ))}
            </ul>
          </div>
          <div>
            <p className="label-small-caps">Queue health</p>
            <ul className="mt-2 space-y-0.5 font-inter text-xs text-cream-soft">
              {slipping.queues.map((q) => (
                <li key={q.studioId} data-testid={`summary-slipping-queue-${q.studioId}`} data-count={q.depth}>
                  {q.name}: <span className="text-cream">{q.depth}</span> waiting
                  {q.oldestIso ? <span className="text-cream-faint"> · oldest {relativeFromIso(q.oldestIso, reportingNow())}</span> : null}
                </li>
              ))}
            </ul>
          </div>
          <div>
            <p className="label-small-caps">Unworked leads</p>
            <p className="font-cinzel text-2xl text-cream mt-1">
              <Figure testId="summary-slipping-unworked" count={slipping.leads.count}>
                {slipping.leads.count.toLocaleString('en-AU')}
              </Figure>
            </p>
            <p className="font-inter text-xs text-cream-soft mt-1">
              {slipping.leads.oldestIso
                ? `New leads nobody has messaged yet · oldest ${relativeFromIso(slipping.leads.oldestIso, reportingNow())}`
                : 'Every new lead has been messaged.'}
            </p>
          </div>
        </div>
      </section>
      ) : null}
    </div>
  );
}

function Tile({ label, info, delta, children }: { label: string; info?: string; delta?: ReactNode; children: ReactNode }) {
  return (
    <div className="panel-dark p-4">
      <p className="label-small-caps flex items-center gap-1">{label}{info ? <InfoTip>{info}</InfoTip> : null}</p>
      <p className="font-cinzel text-3xl text-cream mt-2">{children}</p>
      {delta ? <p className="mt-1">{delta}</p> : null}
    </div>
  );
}

/**
 * Signed % change of a window metric vs the previous equal-length window.
 * Renders nothing when the prior window has no base (can't divide by zero).
 * Up is always the good direction for the metrics we badge here (revenue,
 * deposits, completed, take, leads, avg, rebook rate).
 */
function DeltaBadge({ testid, cur, prev }: { testid: string; cur: number; prev: number }) {
  if (!(prev > 0)) return null;
  const d = Math.round(((cur - prev) / prev) * 100);
  const up = d >= 0;
  return (
    <span data-testid={testid} className={`font-inter text-xs ${up ? 'text-mint' : 'text-blood'}`}>
      {up ? '▲' : '▼'} {Math.abs(d)}% <span className="text-cream-faint">vs last wk</span>
    </span>
  );
}
